import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Code2, Globe, ArrowRight, Menu, X, ChevronDown, CheckCircle,
  Users, Building, Zap, Trophy, Target, Award, Monitor, Smartphone, Server, Database, Cloud
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';

// --- Reused Styled Components (from Home.js) ---

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
  overflow-x: hidden;
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 20px;
  line-height: 1.1;
  text-align: left;
`;

const LastUpdated = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-bottom: 40px;
`;

const Section = styled.div`
  margin-bottom: 40px;
  
  h2 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    color: var(--text-main);
  }
  
  p {
    line-height: 1.8;
    color: var(--text-muted);
    margin-bottom: 16px;
  }
`;

const LangContainer = styled.div`
  position: relative;
  margin-left: 20px;
`;

const LangButton = styled.button`
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const LangItem = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  ${props => props.active && `
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
  `}
`;

const Privacy = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  return (
    <>
      <Navbar />

      <PageContainer>
        <ContentSection>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('privacy.title')}
          </Title>
          <LastUpdated>{t('privacy.lastUpdated')}</LastUpdated>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Section>
              <p>{t('privacy.intro')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.collection.title')}</h2>
              <p>{t('privacy.collection.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.use.title')}</h2>
              <p>{t('privacy.use.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.retention.title')}</h2>
              <p>{t('privacy.retention.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.security.title')}</h2>
              <p>{t('privacy.security.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.rights.title')}</h2>
              <p>{t('privacy.rights.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.cookies.title')}</h2>
              <p>{t('privacy.cookies.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.thirdParty.title')}</h2>
              <p>{t('privacy.thirdParty.text')}</p>
            </Section>

            <Section>
              <h2>{t('privacy.contact.title')}</h2>
              <p>{t('privacy.contact.text')}</p>
            </Section>
          </motion.div>
        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default Privacy;
